import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "../../Components/ui/sonner";
import videoIcon from "../../Assets/icons/video.svg";
import microphoneIcon from "../../Assets/icons/microphone.svg";
import pencilIcon from "../../Assets/icons/pencil.svg";
import okIcon from "../../Assets/icons/ok.svg";
import uploadIcon from "../../Assets/icons/upload.svg";

const GiftDashboard = ({ cardId: propCardId }) => {
  const { giftId: paramGiftId } = useParams();
  const [giftId, setGiftId] = useState(paramGiftId);
  const [messages, setMessages] = useState([]);
  const [globalMessages, setGlobalMessages] = useState([]);
  const [giftSession, setGiftSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("gift"); // 'gift' or 'global'
  const previousMessageCountRef = useRef(0);
  const previousGlobalCountRef = useRef(0);

  const fetchData = async () => {
    try {
      let currentGiftId = giftId;

      // If we have a cardId but no giftId, fetch the session by cardId
      if (!currentGiftId && propCardId) {
        const res = await fetch(
          `${process.env.REACT_APP_PUBLIC_BACKEND_URL}get_gift_session`,
          {
            method: "POST",
            body: JSON.stringify({ cardId: propCardId }),
          }
        );
        const data = await res.json();
        if (data && data.giftId) {
          currentGiftId = data.giftId;
          setGiftId(currentGiftId);
          setGiftSession(data);
        }
      } else if (currentGiftId) {
        // Fetch session details by giftId
        const sessionRes = await fetch(
          `${process.env.REACT_APP_PUBLIC_BACKEND_URL}get_gift_session`,
          {
            method: "POST",
            body: JSON.stringify({ giftId: currentGiftId }),
          }
        );
        const sessionData = await sessionRes.json();
        setGiftSession(sessionData);
      }

      if (currentGiftId) {
        // Fetch messages
        const messagesRes = await fetch(
          `${process.env.REACT_APP_PUBLIC_BACKEND_URL}get_moderation_messages`,
          {
            method: "POST",
            body: JSON.stringify({ giftId: currentGiftId }),
          }
        );
        const messagesData = await messagesRes.json();
        const newMessages = Array.isArray(messagesData)
          ? messagesData
          : messagesData.messages || [];

        // Check for new messages and show notification
        const prevCount = previousMessageCountRef.current;
        if (newMessages.length > prevCount && prevCount > 0) {
          const newCount = newMessages.length - prevCount;
          toast.success(
            `${newCount} new message${newCount > 1 ? "s" : ""} received!`
          );
        }
        setMessages(newMessages);
        previousMessageCountRef.current = newMessages.length;

        // Fetch global messages
        const globalRes = await fetch(
          `${process.env.REACT_APP_PUBLIC_BACKEND_URL}get_global_kindness`,
          {
            method: "POST",
            body: JSON.stringify({ giftId: currentGiftId }),
          }
        );
        const globalData = await globalRes.json();
        const newGlobalMessages = Array.isArray(globalData)
          ? globalData
          : globalData.messages || [];

        // Check for new global messages and show notification
        const prevGlobalCount = previousGlobalCountRef.current;
        if (newGlobalMessages.length > prevGlobalCount && prevGlobalCount > 0) {
          const newCount = newGlobalMessages.length - prevGlobalCount;
          toast.info(
            `${newCount} new external wish${
              newCount > 1 ? "es" : ""
            } available!`
          );
        }
        setGlobalMessages(newGlobalMessages);
        previousGlobalCountRef.current = newGlobalMessages.length;
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchData();

    // Set up polling every 5 seconds
    const pollInterval = setInterval(() => {
      // Only poll if page is visible (not in background tab)
      if (!document.hidden) {
        fetchData();
      }
    }, 5000); // Poll every 5 seconds

    // Clean up interval on unmount
    return () => clearInterval(pollInterval);
  }, [paramGiftId, propCardId]);

  // Reset previous counts when giftId changes
  useEffect(() => {
    previousMessageCountRef.current = 0;
    previousGlobalCountRef.current = 0;
  }, [giftId]);

  const handleModerate = async (messageId, status, isGlobal) => {
    try {
      await fetch(
        `${process.env.REACT_APP_PUBLIC_BACKEND_URL}moderate_message`,
        {
          method: "POST",
          body: JSON.stringify({ messageId, status, isGlobal, giftId }),
        }
      );
      // Optimistic update
      if (isGlobal) {
        setGlobalMessages(
          globalMessages.map((m) =>
            m._id === messageId ? { ...m, status } : m
          )
        );
      } else {
        setMessages(
          messages.map((m) => (m._id === messageId ? { ...m, status } : m))
        );
      }
    } catch (error) {
      console.error("Error moderating message:", error);
    }
  };

  const handleToggleAutoApprove = async () => {
    try {
      const newValue = !giftSession.autoApprove;
      await fetch(
        `${process.env.REACT_APP_PUBLIC_BACKEND_URL}toggle_auto_approve`,
        {
          method: "POST",
          body: JSON.stringify({ giftId, autoApprove: newValue }),
        }
      );
      setGiftSession({ ...giftSession, autoApprove: newValue });
      toast.success(
        newValue ? "Auto-approve enabled" : "Auto-approve disabled"
      );
    } catch (error) {
      console.error("Error toggling auto-approve:", error);
      toast.error("Failed to update setting");
    }
  };

  const handleActivate = async () => {
    try {
      await fetch(`${process.env.REACT_APP_PUBLIC_BACKEND_URL}activate_gift`, {
        method: "POST",
        body: JSON.stringify({ giftId }),
      });
      setGiftSession({ ...giftSession, activated: true });
      toast.success(
        "Gift Card Activated! Tapping the card will now show the Gift Feed."
      );
    } catch (error) {
      console.error("Error activating gift:", error);
    }
  };

  if (loading)
    return (
      <div className="p-12 text-center text-gray-400 font-serif">
        Loading gift details...
      </div>
    );
  if (!giftSession)
    return (
      <div className="p-12 text-center text-gray-400 font-serif">
        Gift session not found.
      </div>
    );

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-gray-100">
      <Toaster position="top-center" richColors />
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header Section */}
        <header className="mb-12 border-b border-gray-100 pb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-serif text-gray-900 mb-2">
                For {giftSession.recipientName || "Someone Special"}
              </h1>
              <p className="text-gray-500 font-light text-lg">
                {giftSession.occasion} • {messages.length} messages collected
              </p>
            </div>

            <div className="flex flex-col items-end gap-3">
              <div
                className={`px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wide ${
                  giftSession.activated
                    ? "bg-green-50 text-green-700 border border-green-100"
                    : "bg-green-50 text-green-700 border border-green-100"
                }`}
              >
                Active
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => window.open(`/gift/${giftSession.cardId}`, "_blank")}
                  className="bg-white border border-gray-200 text-gray-900 px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all"
                >
                  Preview
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Share Section */}
        <section className="mb-12 bg-gray-50 rounded-xl p-6 border border-gray-100">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <img src={uploadIcon} alt="" className="w-5 h-5" /> Share this link
          </h3>
          <div className="space-y-3">
            <input
              readOnly
              value={`${window.location.origin}/gift/${giftSession.cardId || giftId}/contribute`}
              className="w-full bg-white border-gray-200 border rounded-lg px-4 py-3 text-gray-600 font-mono text-sm focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/gift/${giftSession.cardId || giftId}/contribute`
                  );
                  toast.success("Link copied to clipboard!");
                }}
                className="flex-1 bg-white border border-gray-200 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Copy Link
              </button>
              <button
                onClick={() => {
                  const shareUrl = `${window.location.origin}/gift/${giftSession.cardId || giftId}/contribute`;
                  const shareText = `Hey! I'm preparing a gift for ${
                    giftSession?.recipientName || "someone special"
                  }. Can you leave a quick message here? ${shareUrl}`;

                  if (navigator.share) {
                    navigator
                      .share({
                        title: `Gift for ${
                          giftSession?.recipientName || "someone special"
                        }`,
                        text: shareText,
                        url: shareUrl,
                      })
                      .catch((err) => console.log("Share cancelled"));
                  } else {
                    // Fallback: copy to clipboard
                    navigator.clipboard.writeText(shareText);
                    toast.success("Message copied to clipboard!");
                  }
                }}
                className="flex-1 bg-black text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Share
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-400 mt-3">
            Send this link to friends and family so they can add their messages.
          </p>
        </section>

        {/* Auto-Approve Toggle */}
        <section className="mb-12 bg-gray-50 rounded-xl p-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">
                Auto-Approve Messages
              </h3>
              <p className="text-sm text-gray-500">
                Automatically approve messages from people who use your link
                (not global messages)
              </p>
            </div>
            <button
              onClick={handleToggleAutoApprove}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                giftSession.autoApprove !== false ? "bg-black" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  giftSession.autoApprove !== false ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </section>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("gift")}
            className={`px-4 py-3 font-medium text-sm transition-all relative ${
              activeTab === "gift"
                ? "text-black border-b-2 border-black"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            My Gift
            <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {messages.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("global")}
            className={`px-4 py-3 font-medium text-sm transition-all relative ${
              activeTab === "global"
                ? "text-black border-b-2 border-black"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            External Wishes
            <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {globalMessages.length}
            </span>
          </button>
        </div>

        {/* Messages Section */}
        <section>
          <h2 className="text-2xl font-serif text-gray-900 mb-6 flex items-center gap-3">
            {activeTab === "gift" ? "Messages" : "External Wishes"}
          </h2>

          <div className="space-y-4">
            {(activeTab === "gift" ? messages : globalMessages).map((msg) => (
              <div
                key={msg._id}
                className="group bg-white border border-gray-100 rounded-xl p-5 transition-all duration-300 hover:border-gray-200"
              >
                <div className="flex flex-col gap-6">
                  {/* Content Preview */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-semibold text-gray-900">
                        {msg.senderName || "Anonymous"}
                      </span>
                      <span className="text-xs text-gray-400 uppercase tracking-wider flex items-center gap-1">
                        •
                        {msg.type === "video" && (
                          <img
                            src={videoIcon}
                            alt="Video"
                            className="w-3 h-3 opacity-50"
                          />
                        )}
                        {msg.type === "audio" && (
                          <img
                            src={microphoneIcon}
                            alt="Audio"
                            className="w-3 h-3 opacity-50"
                          />
                        )}
                        {msg.type === "text" && (
                          <img
                            src={pencilIcon}
                            alt="Text"
                            className="w-3 h-3 opacity-50"
                          />
                        )}
                        {msg.type === "image" && (
                          <img
                            src={uploadIcon}
                            alt="Image"
                            className="w-3 h-3 opacity-50"
                          />
                        )}
                        {msg.type}
                      </span>
                      {msg.isGlobal && (
                        <span className="text-xs bg-pink-50 text-pink-600 px-2 py-0.5 rounded-full">
                          Global Pool
                        </span>
                      )}
                    </div>

                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      {msg.type === "text" && (
                        <p className="p-4 text-gray-700 italic font-serif leading-relaxed">
                          "{msg.textContent}"
                        </p>
                      )}
                      {msg.type === "video" && (
                        <video
                          src={msg.contentUrl}
                          controls
                          className="w-full max-h-64 object-cover bg-black"
                        />
                      )}
                      {msg.type === "image" && (
                        <img
                          src={msg.contentUrl}
                          alt="Upload"
                          className="w-full max-h-64 object-cover"
                        />
                      )}
                      {msg.type === "audio" && (
                        <div className="p-4 flex items-center justify-center">
                          <audio
                            src={msg.contentUrl}
                            controls
                            className="w-full"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Moderation Controls */}
                  <div className="flex gap-2 w-full">
                    <button
                      onClick={() =>
                        handleModerate(
                          msg._id,
                          "approved",
                          activeTab === "global"
                        )
                      }
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        msg.status === "approved"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-50 text-gray-500 hover:bg-green-50 hover:text-green-600"
                      }`}
                    >
                      {msg.status === "approved" ? "Approved" : "Approve"}
                    </button>
                    <button
                      onClick={() =>
                        handleModerate(
                          msg._id,
                          "rejected",
                          activeTab === "global"
                        )
                      }
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        msg.status === "rejected"
                          ? "bg-red-100 text-red-700"
                          : "bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-600"
                      }`}
                    >
                      {msg.status === "rejected" ? "Rejected" : "Reject"}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {(activeTab === "gift" ? messages : globalMessages).length ===
              0 && (
              <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <p className="text-gray-400 mb-2">
                  {activeTab === "gift"
                    ? "No messages yet"
                    : "No external wishes available"}
                </p>
                <p className="text-sm text-gray-300">
                  {activeTab === "gift"
                    ? "Share the link above to get started"
                    : "External wishes are messages from strangers in the global pool. Approve them to include in the gift feed."}
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default GiftDashboard;
